import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MONTHLY_FIELDS2, MONTHLY_FIELDS } from "./Monthly";
import { DAILY_FIELDS2, DAILY_FIELDS } from "./Daily";
import { COMP_FIELDS2, COMP_FIELDS } from "./Compensation";
import { MENU_EXPORT_COLUMNS, buildMenuExportRows } from "./menuExportRows";

function flattenTopLevel(obj) {
  const result = {};
  for (let key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const val = obj[key];
    if (
      (Array.isArray(val) || (val && typeof val === "object")) &&
      key !== "date"
    )
      continue;
    result[key] = formatValue(val, key);
  }
  return result;
}

function formatValue(value, key = "") {
  // Don't format if key suggests ID or phone or amount
  const lowerKey = key.toLowerCase();
  if (!lowerKey.includes("date")) {
    return value;
  }

  if (value && typeof value.toDate === "function") {
    const date = value.toDate();
    return date.toISOString().replace("T", " ").slice(0, 19);
  }
  // Convert valid ISO date strings
  if (typeof value === "string" && !isNaN(Date.parse(value))) {
    const date = new Date(value);
    return date.toISOString().replace("T", " ").slice(0, 19);
  }

  // Convert numeric Unix timestamps (in milliseconds)
  if (typeof value === "number" && value > 1000000000000) {
    const date = new Date(value);
    if (!isNaN(date)) {
      return date.toISOString().replace("T", " ").slice(0, 19);
    }
  }

  return value;
}

function flattenWeeklySchedule(days) {
  const result = {};

  for (const day in days) {
    const dayData = days[day];
    for (const period in dayData) {
      const periodData = dayData[period];
      result[`${day}_${period}_from`] = periodData.from || "";
      result[`${day}_${period}_to`] = periodData.to || "";
    }
  }

  return result;
}

export function exportRestaurantsToExcel(restaurants) {
  const workbook = XLSX.utils.book_new();

  // Main Sheet: Restaurants
  const restaurantMainData = restaurants.map((r) => ({
    ...flattenTopLevel(r),
    ...flattenTopLevel(r.details || {}),
  }));
  const restaurantSheet = XLSX.utils.json_to_sheet(restaurantMainData);
  XLSX.utils.book_append_sheet(workbook, restaurantSheet, "Restaurants");

  // Sub-Sheets
  let branchesSheetData = [],
    branchAppsSheetData = [],
    financialSheetData = [],
    branchesUserInfoSheetData = [];
  let workTimeSheetData = [],
    userInfoSheetData = [];

  restaurants.forEach((rest) => {

    if (Array.isArray(rest.branches)) {
      rest.branches.forEach((branch, i) => {
        const flatBranch = {
          id: rest.id,
          businessName: rest.businessName,
          ...flattenTopLevel(branch),
        };
        branchesSheetData.push(flatBranch);

        if (Array.isArray(branch.apps)) {
          branch.apps.forEach((app) => {
            const flatApp = {
              RestId: rest.id,
              businessName: rest.businessName,
              branchName: branch.name,
              ...flattenTopLevel(app),
            };
            branchAppsSheetData.push(flatApp);
          });
        }

        if (Array.isArray(branch.financial)) {
          branch.financial.forEach((f) => {
            const flatFinance = {
              RestId: rest.id,
              businessName: rest.businessName,
              branchName: branch.name,
              ...flattenTopLevel(f),
            };
            financialSheetData.push(flatFinance);
          });
        }

        if (Array.isArray(branch.userInfo)) {
          branch.userInfo.forEach((user) => {
            const flatUserInfo = {
              RestId: rest.id,
              businessName: rest.businessName,
              branchName: branch.name,
              ...flattenTopLevel(user),
            };
            branchesUserInfoSheetData.push(flatUserInfo);
          });
        }
        if (Array.isArray(branch.workTime)) {
          branch.workTime.forEach((wt) => {
            const flatUserInfo = {
              RestId: rest.id,
              businessName: rest.businessName,
              branchName: branch.name,
              name: wt.name,
              ...flattenWeeklySchedule(wt.days),
            };
            workTimeSheetData.push(flatUserInfo);
          });
        }
      });
    }
    if (Array.isArray(rest.userInfo)) {
      rest.userInfo.forEach((user) => {
        const flatUserInfo = {
          RestId: rest.id,
          businessName: rest.businessName,
          ...user,
        };
        userInfoSheetData.push(flatUserInfo);
      });
    }
  });

  if (branchesSheetData.length)
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(branchesSheetData),
      "Branches",
    );

  if (branchAppsSheetData.length)
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(branchAppsSheetData),
      "Branches Apps",
    );

  if (financialSheetData.length)
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(financialSheetData),
      "Financial",
    );

  if (branchesUserInfoSheetData.length)
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(branchesUserInfoSheetData),
      "Branch UserInfo",
    );
  if (workTimeSheetData.length)
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(workTimeSheetData),
      "Branch Work Time",
    );
  if (userInfoSheetData.length)
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(userInfoSheetData),
      "user Information",
    );

  // Export to file
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, "store_data.xlsx");
}

// export function createExcelWithSheets(sheetsData, fileName = `report-${Date.now()}.xlsx`) {
//     const wb = XLSX.utils.book_new();

//     const sheetNames = { compensation: "التفاصيل", Daily: "اليومي", Monthly: "الشهري" }

//     sheetsData.forEach(({ name, data }, index) => {
//         const ws = XLSX.utils.json_to_sheet(data);
//         const sheetName = sheetNames[name] || name || `Sheet${index + 1}`;

//         // Set column widths - here 20 characters for each column
//         const colsCount = data.length > 0 ? Object.keys(data[0]).length : 1;
//         ws['!cols'] = new Array(colsCount).fill({ wch: 10 }); // wch = width in chars

//         XLSX.utils.book_append_sheet(wb, ws, sheetName);
//     });

//     XLSX.writeFile(wb, fileName);
// }

export function exportToExcel(dataArray, name) {
  if (!Array.isArray(dataArray) || dataArray.length === 0 || !name) {
    console.error("Invalid data or name");
    return;
  }

  let orderedKeys = [];
  let labelMap = {};

  if (name === "Monthly") {
    orderedKeys = [
      MONTHLY_FIELDS2.date,
      MONTHLY_FIELDS2.storeName,
      MONTHLY_FIELDS2.appKey,
      MONTHLY_FIELDS2.branchName,
      MONTHLY_FIELDS2.appBranchId,
      MONTHLY_FIELDS2.app,
      MONTHLY_FIELDS2.completed,
      MONTHLY_FIELDS2.completedAmount,
      MONTHLY_FIELDS2.rejected,
      MONTHLY_FIELDS2.rejectedAmount,
      MONTHLY_FIELDS2.canceled,
      MONTHLY_FIELDS2.canceledAmount,
      MONTHLY_FIELDS2.refund,
      MONTHLY_FIELDS2.refundAmount,
      MONTHLY_FIELDS2.endurance,
      MONTHLY_FIELDS2.enduranceAmount,
      MONTHLY_FIELDS2.serviceCharge,
      MONTHLY_FIELDS2.deliveryOffer,
      MONTHLY_FIELDS2.offers,
      MONTHLY_FIELDS2.ccAmount,
      MONTHLY_FIELDS2.posDevice,
      MONTHLY_FIELDS2.vat,
      MONTHLY_FIELDS2.grandTotal,
    ];
    labelMap = MONTHLY_FIELDS;
  } else if (name === "Daily") {
    orderedKeys = [
      DAILY_FIELDS2.date,
      DAILY_FIELDS2.storeName,
      DAILY_FIELDS2.appKey,
      DAILY_FIELDS2.branchName,
      DAILY_FIELDS2.appBranchId,
      DAILY_FIELDS2.app,
      DAILY_FIELDS2.completed,
      DAILY_FIELDS2.completedAmount,
      DAILY_FIELDS2.rejected,
      DAILY_FIELDS2.canceled,
      DAILY_FIELDS2.refund,
      DAILY_FIELDS2.endurance,
    ];
    labelMap = DAILY_FIELDS;
  } else if (name === "Details") {
    orderedKeys = [
      COMP_FIELDS2.date,
      COMP_FIELDS2.time,
      COMP_FIELDS2.storeName,
      COMP_FIELDS2.appKey,
      COMP_FIELDS2.branchName,
      COMP_FIELDS2.appBranchId,
      COMP_FIELDS2.app,
      COMP_FIELDS2.orderId,
      COMP_FIELDS2.orderStatus,
      COMP_FIELDS2.orderAmount,
      COMP_FIELDS2.enduranceAmount,
      COMP_FIELDS2.refundAmount,
      COMP_FIELDS2.cause,
    ];
    labelMap = COMP_FIELDS;
  }
  // Rebuild data in correct order with only wanted fields
  const cleanedData = dataArray.map((item) => {
    const newItem = {};
    orderedKeys.forEach((key) => {
      newItem[key] = item[key] ?? "";
    });
    return newItem;
  });
  // Convert to worksheet without headers
  const worksheet = XLSX.utils.json_to_sheet(cleanedData, {
    header: orderedKeys,
    skipHeader: true,
    origin: "A2",
  });

  // Add Arabic header row
  const headerRow = orderedKeys.map((key) => labelMap[key] || key);
  XLSX.utils.sheet_add_aoa(worksheet, [headerRow], { origin: "A1" });

  // Create workbook and append sheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, name);

  // Generate filename
  const date = Date.now();
  const fileName = `${name}-${date}.xlsx`;

  // Trigger download
  XLSX.writeFile(workbook, fileName);
}

// Same layout as before (so exported files can be re-imported) plus a
// "Prices" sheet with every pricing entry, its discount and effective price.
export function handleExportMenuExcel(menu) {
  const wb = XLSX.utils.book_new();
  // Row building lives in menuExportRows.js (mirrored by the backend's attachment export).
  const { rows, priceRows, merges } = buildMenuExportRows(menu);

  const sheet = XLSX.utils.json_to_sheet(rows, { header: MENU_EXPORT_COLUMNS });
  sheet["!merges"] = merges;
  XLSX.utils.book_append_sheet(wb, sheet, "Menu");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(priceRows), "Prices");

  const restaurantName = menu.restaurant?.businessName || "Restaurant";
  XLSX.writeFile(wb, `${restaurantName}_${menu.name || "Menu"}.xlsx`);
}

export const readMenuFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // assume first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "", // avoid undefined
        });

        resolve(jsonData);
      } catch (err) {
        reject(err);
      }
    };

    reader.readAsArrayBuffer(file);
  });
};

// Spreadsheet rows -> { sections } for the menu import endpoints (draft
// menus only). Merged cells are forward-filled. The "cost" column becomes the
// "base" price. Addition columns are not imported here: additions are
// restaurant-level and uploaded from the additions panel (the legacy import
// wrote them into an item field that was never stored).
export const transformMenuData = (rows) => {
  const sections = [];
  const sectionByKey = new Map();
  const current = { section_en: "", section_ar: "", item_en: "", item_ar: "" };

  rows.forEach((row) => {
    if (row.section_en || row.section_ar) {
      current.section_en = String(row.section_en || "").trim();
      current.section_ar = String(row.section_ar || "").trim();
    }
    if (row.item_en || row.item_ar) {
      current.item_en = String(row.item_en || "").trim();
      current.item_ar = String(row.item_ar || "").trim();
    } else if (row.section_en || row.section_ar) {
      current.item_en = "";
      current.item_ar = "";
    }
    if (!(current.section_en || current.section_ar) || !(current.item_en || current.item_ar)) return;

    const sectionKey = `${current.section_en}|${current.section_ar}`;
    if (!sectionByKey.has(sectionKey)) {
      const created = { name: { en: current.section_en, ar: current.section_ar }, items: [], itemKeys: new Set() };
      sectionByKey.set(sectionKey, created);
      sections.push(created);
    }
    const section = sectionByKey.get(sectionKey);
    const itemKey = `${current.item_en}|${current.item_ar}`;
    if (section.itemKeys.has(itemKey)) return;
    section.itemKeys.add(itemKey);
    const cost = row.cost === "" || row.cost === undefined || row.cost === null ? null : Number(row.cost);
    section.items.push({
      name: { en: current.item_en, ar: current.item_ar },
      description: { en: String(row.desc_en || ""), ar: String(row.desc_ar || "") },
      allergens: { en: String(row.allergen_en || ""), ar: String(row.allergen_ar || "") },
      calories: { en: row.callories ? String(row.callories) : "", ar: row.callories ? String(row.callories) : "" },
      pricing: Number.isFinite(cost) ? [{ name: "base", price: cost, discount: null }] : [],
      isBox: false,
    });
  });

  return { sections: sections.map(({ itemKeys, ...section }) => section) };
};
